import { initialLanguageOption } from 'constants/languages';
import { ModalTypes } from 'enums/modalTypes';
import {
  SupportedLocale as SupportedLocaleType,
  useGetAssessmentStageInviteUrlLazyQuery,
  useProjectBaseGetProjectByIdQuery,
} from 'generated/graphql';
import { closeModal } from 'graphql/modals';
import { FC, useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  getSupportedLocaleMetadata,
  SupportedLocale,
} from '@sz/l10n';
import {
  Button,
  SelectFormField,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';

interface ICopyStageIndirectInvitation {
  stageId: string;
  projectId: number;
}

const CopyStageIndirectInvitation: FC<ICopyStageIndirectInvitation> = ({
  stageId,
  projectId,
}) => {
  const { handleMsgType } = useNotification();
  const [language, setLanguage] = useState<
    TSelectOption<keyof typeof SupportedLocale>
  >(initialLanguageOption);

  const getProjectByIdResponse = useProjectBaseGetProjectByIdQuery({
    variables: { id: Number(projectId) },
    onError: error =>
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      }),
  });
  const projectLanguages = getProjectByIdResponse?.data?.project?.locales;

  const [inviteUrlQuery, inviteUrlQueryResponse] =
    useGetAssessmentStageInviteUrlLazyQuery({
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
        closeModal(ModalTypes.STAGE_INDIRECT_INVITATION_MODAL);
      },
    });

  useEffect(() => {
    inviteUrlQuery({
      variables: {
        id: stageId,
        locale: initialLanguageOption.value as SupportedLocaleType,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeLanguage = (
    value: TSelectOption<keyof typeof SupportedLocale>
  ) => {
    setLanguage(value);
    if (value) {
      inviteUrlQuery({
        variables: {
          id: stageId,
          locale: value.value as SupportedLocaleType,
        },
      });
    }
  };

  const url = inviteUrlQueryResponse.data?.assessmentStage?.inviteUrl;
  const copyToClipboardText = url || '';

  const handleCopy = () => {
    if (url) {
      handleMsgType({
        type: TNotification.success,
        title: `Link copied successfully`,
      });
    }
  };

  const languageSelectFieldOptions = projectLanguages?.length
    ? projectLanguages.map(locale => {
      return {
        label: getSupportedLocaleMetadata(SupportedLocale[locale]).name
          .english,
        value: locale,
      };
    })
    : [initialLanguageOption];

  return (
    <div className="copy-stage-indirect-invitation">
      <SelectFormField
        value={language}
        onChange={handleChangeLanguage}
        id={`stage-indirect-invitation-language-select-${stageId}`}
        label="Language"
        options={languageSelectFieldOptions}
        placeholder="Language"
        data-testid="indirect-invite-candidate__language-select-form"
        hasClearIndicator={false}
      />
      <CopyToClipboard text={copyToClipboardText}>
        <Button
          size="medium"
          onClick={handleCopy}
          disabled={!url}
          data-testid="indirect-invite-candidate__copy-url-buttton"
        >
          Copy link
        </Button>
      </CopyToClipboard>
    </div>
  );
};

export default CopyStageIndirectInvitation;
